import { Blueprint } from '@/components/ui/Blueprint';
import { CardKicker } from '@/components/ui/Card';
import { MonoData } from '@/components/ui/MonoData';
import { ptBR } from '@/i18n/pt-BR';
import type { SignatureRecordData } from '@/lib/case_signatures';
import { useSignatureTopography, type SignatureTopographyState } from './useSignatureTopography';

export interface TopographyPanelProps {
  caseId: string;
  contested: SignatureRecordData;
  selectedPattern: SignatureRecordData | null;
  /** Controlado pelo seletor segmentado (E29.F1): o corpo só monta quando aberto. */
  open: boolean;
}

/**
 * Topografia de pressão estimada — K|Q lado a lado (E26.F1/ADR-0036). // ux-neutrality:allow doc-comment
 *
 * Painel colapsado por padrão (o mapa é computado on-demand no backend; só // ux-neutrality:allow doc-comment
 * busca ao expandir). Honestidade por construção: rótulo "estimada" no // ux-neutrality:allow doc-comment
 * título + nota metodológica sempre visíveis; imagem sem base para o mapa // ux-neutrality:allow doc-comment
 * exibe estado indisponível (422 do backend) em vez de mapa fingido; nenhum // ux-neutrality:allow doc-comment
 * exame comparativo automático entre os dois mapas — a leitura K|Q é do // ux-neutrality:allow doc-comment
 * perito (ADR-0026 preservada). // ux-neutrality:allow doc-comment
 *
 * E54.F6.T06 — o interior do painel passa à prancha 2C: card de blueprint com // ux-neutrality:allow doc-comment
 * kicker, nota metodológica, a rampa FINO→ESPESSO que diz o que a cor do mapa // ux-neutrality:allow doc-comment
 * significa e as duas figuras em moldura com legenda mono. // ux-neutrality:allow doc-comment
 */
export function TopographyPanel({
  caseId,
  contested,
  selectedPattern,
  open,
}: TopographyPanelProps): JSX.Element | null {
  if (!open) return null;

  return (
    <section className="topography-panel" aria-label={ptBR.workspaceCaso.topographyTitle}>
      <TopographyMaps caseId={caseId} contested={contested} selectedPattern={selectedPattern} />
    </section>
  );
}

/**
 * A escala da leitura: o mapa é renderizado no backend numa rampa quente, e sem
 * a legenda a cor não diz nada. Mesma rampa do pacote (aviso → negativo), que é
 * o que a prancha 2C desenha.
 */
function TopographyRamp(): JSX.Element {
  return (
    <div className="flex items-center gap-sc-2" data-testid="topography-ramp">
      <MonoData variant="label">{ptBR.workspaceCaso.topographyRampThin}</MonoData>
      <span
        aria-hidden
        className="block h-2.5 w-[150px] border border-border bg-gradient-to-r from-warning-50 via-warning to-danger-700"
      />
      <MonoData variant="label">{ptBR.workspaceCaso.topographyRampThick}</MonoData>
    </div>
  );
}

function TopographyMaps({
  caseId,
  contested,
  selectedPattern,
}: Omit<TopographyPanelProps, 'open'>): JSX.Element {
  const patternTopography = useSignatureTopography(caseId, selectedPattern?.id);
  const contestedTopography = useSignatureTopography(caseId, contested.id);

  return (
    <Blueprint className="topography-body" id="topography-body" data-testid="topography-body">
      <div className="flex flex-wrap items-end justify-between gap-sc-4">
        <div className="min-w-0">
          <CardKicker>{ptBR.workspaceCaso.topographyTitle}</CardKicker>
          <p className="mt-1 max-w-prose text-body-sm text-fg-muted">
            {ptBR.workspaceCaso.topographyMethodNote}
          </p>
        </div>
        <TopographyRamp />
      </div>
      {/* A rolagem fica no filho, não na moldura: só a faixa das duas figuras
          rola na horizontal, e o cabeçalho do painel continua parado. */}
      <div className="mt-sc-4 overflow-x-auto">
        <div className="side-by-side">
          <TopographyFigure
            topography={patternTopography}
            caption={ptBR.workspaceCaso.topographyPatternCaption}
            testId="topography-pattern"
          />
          <TopographyFigure
            topography={contestedTopography}
            caption={ptBR.workspaceCaso.topographyContestedCaption}
            testId="topography-contested"
          />
        </div>
      </div>
      <p className="topography-disclaimer">{ptBR.workspaceCaso.topographyDisclaimer}</p>
    </Blueprint>
  );
}

interface TopographyFigureProps {
  topography: SignatureTopographyState;
  caption: string;
  testId: string;
}

function TopographyFigure({ topography, caption, testId }: TopographyFigureProps): JSX.Element {
  return (
    <Blueprint as="figure" className="topography-figure" data-testid={testId}>
      <TopographyFigureBody topography={topography} caption={caption} />
      <figcaption className="border-t border-border px-sc-3 py-sc-2">
        <MonoData variant="label">{caption}</MonoData>
      </figcaption>
    </Blueprint>
  );
}

function TopographyFigureBody({
  topography,
  caption,
}: Pick<TopographyFigureProps, 'topography' | 'caption'>): JSX.Element {
  if (topography.url !== null) {
    return (
      <img
        src={topography.url}
        alt={`${ptBR.workspaceCaso.topographyTitle} — ${caption}`}
        className="signature-img"
      />
    );
  }
  // Classe F-E26.F3-02: falha técnica NÃO se apresenta como "indisponível" —
  // indisponibilidade é estado forense do material, não da operação.
  if (topography.error) {
    return (
      <p className="topography-error" role="alert" data-testid="topography-error">
        {ptBR.workspaceCaso.topographyError}
      </p>
    );
  }
  if (topography.unavailable) {
    return <p className="topography-unavailable">{ptBR.workspaceCaso.topographyUnavailable}</p>;
  }
  if (topography.loading) {
    return (
      <div className="signature-img min-h-[12rem] animate-pulse bg-fg/[0.06]" role="status">
        <span className="sr-only">{ptBR.workspaceCaso.topographyLoading}</span>
      </div>
    );
  }
  return <p className="topography-unavailable">{ptBR.workspaceCaso.topographyUnavailable}</p>;
}
